<template>
  <div class="container" @mouseleave="selectedValue = undefined">
    <div class="navigation">
      <div class="tokens">
        <button
          :class="{ active: currentChart === 0 && tokenB?.type }"
          @click="currentChart = 0"
        >
          <TokenIcon
            :logo="tokenA?.logo"
            :type="tokenA?.type"
            class="token-img"
            size="24"
          />
          <span>
            {{ tokenA?.symbol }}
          </span>
        </button>

        <button
          v-if="tokenB?.type"
          :class="{ active: currentChart === 1 }"
          @click="currentChart = 1"
        >
          <TokenIcon
            :logo="tokenB?.logo"
            :type="tokenB?.type"
            class="token-img"
            size="24"
          />
          <span>
            {{ tokenB?.symbol }}
          </span>
        </button>
      </div>

      <button class="close-mobile" @click="isPriceChartShown = false">
        <span class="pi pi-times p-button-icon" />
      </button>
      <div class="sub-navigation sub-navigation_desktop">
        <ChartNavigation
          :is-known-data="isKnownData"
          :is-price-chart-first-loading="isPriceChartFirstLoading"
          :price-chart-period="priceChartPeriod"
          @update:price-chart-period="setPriceChartPeriod"
          @update:close-price-chart="closePriceChart"
        />
      </div>
    </div>

    <div class="price">
      <div
        v-if="!isPriceChartFirstLoading && isKnownData"
        class="current-price"
      >
        {{ currentPrice }}
      </div>
      <Skeleton
        v-if="isPriceChartFirstLoading"
        class="mb-2"
        height="40px"
        width="200px"
      />

      <span
        v-if="
          isPriceChartReady &&
          isKnownData &&
          !Number.isNaN(selectedDiff?.result)
        "
        :class="['diff-price', selectedDiff.direction === -1 ? 'down' : 'up']"
      >
        {{ selectedDiff.direction === -1 ? '-' : '+' }}
        {{ Math.abs(Number(selectedDiff?.result?.toFixed(2) || 0)) }}%
      </span>
      <Skeleton v-if="!isPriceChartReady" height="18px" width="60px" />
    </div>

    <div v-if="!isPriceChartReady && !isError" class="msg-wrapper">
      <ProgressSpinner
        :style="{ width: '60px', height: '60px' }"
        stroke-width="2px"
      />
    </div>

    <div v-if="isError" class="msg-wrapper">
      <div class="msg-container">
        <span>{{ $t('errors.somethingWentWrongPleaseTryAgain') }}</span>
        <p-button class="p-button-primary" @click="clearCache">
          {{ $t('common.tryAgain') }}
        </p-button>
      </div>
    </div>

    <div v-if="!isKnownData" class="msg-wrapper">
      <div class="msg-container">{{ $t('tokens.noData') }}</div>
    </div>
    <div v-if="isStartPriceError" class="msg-wrapper">
      <div class="msg-container">{{ $t('errors.chartPriceUnavailable') }}</div>
    </div>

    <VueChart
      v-if="isPriceChartReady && isKnownData"
      :class="{ chart_blurred: isStartPriceError }"
      :data="chartData"
      :options="chartOptions"
      class="chart"
      type="line"
    />

    <div class="sub-navigation sub-navigation_mobile">
      <ChartNavigation
        :is-known-data="isKnownData"
        :is-price-chart-first-loading="isPriceChartFirstLoading"
        :price-chart-period="priceChartPeriod"
        @update:price-chart-period="setPriceChartPeriod"
        @update:close-price-chart="closePriceChart"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import VueChart from 'primevue/chart';
import { computed, ref, watch } from 'vue';
import { Chart, ChartTypeRegistry, ScriptableTooltipContext } from 'chart.js';
import { CrosshairPlugin } from 'chartjs-plugin-crosshair';
import Skeleton from 'primevue/skeleton';
import { TPeriod } from '@/constants/price-chart';
import { TokenIcon } from '@/components/UI/TokenIcon';
import { usePriceChart } from '@/composables/usePriceChart';
import ChartNavigation from '@/components/PriceChart/ChartNavigation.vue';

Chart.register(CrosshairPlugin);

const {
  isError,
  clearCache,
  isPriceChartReady,
  isPriceChartFirstLoading,
  isPriceChartShown,
  priceChartPeriod,
  priceChartData,
  tokenA,
  tokenB,
  currentChart,
  isKnownData,
} = usePriceChart();

const selectedValue = ref();

const tmpCtx = document.createElement('canvas').getContext('2d');
let gradient: CanvasGradient;

if (tmpCtx) {
  gradient = tmpCtx.createLinearGradient(0, 0, 0, 400);
  gradient.addColorStop(0.5, 'rgba(3, 149, 129, 0.27)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
}

watch(currentChart, () => {
  selectedValue.value = undefined;
});

const chartData = computed(() =>
  getChartData(priceChartData.value?.labels, priceChartData.value?.prices),
);

const chartOptions = computed(() => getChartOptions());
const selectedDiff = computed(() => {
  const target = selectedValue.value || priceChartData.value?.lastPrice;
  const source = priceChartData.value?.startPrice;
  const result = ((target - source) / source) * 100;
  const direction = result < 0 ? -1 : 1;

  return {
    result,
    direction,
  };
});
const currentPrice = computed(() => {
  const price = selectedValue?.value || priceChartData?.value?.lastPrice;

  return new Intl.NumberFormat('en-US', {
    maximumSignificantDigits: 5,
    style: 'currency',
    currency: 'USD',
  }).format(price);
});

const isStartPriceError = computed(() => {
  return !priceChartData.value?.startPrice;
});

const tooltipHandler = (
  context: ScriptableTooltipContext<keyof ChartTypeRegistry>,
) => {
  selectedValue.value = context?.tooltip?.body?.[0].lines?.[0];
};

const getChartData = (labels: string[], data: number[]) => {
  const documentStyle = getComputedStyle(document.documentElement);

  return {
    labels,
    datasets: [
      {
        data,
        fill: true,
        borderColor: documentStyle.getPropertyValue('--accent-success'),
        borderWidth: 2,
        tension: 0.4,
        backgroundColor: gradient,
        // parsing: false,
      },
    ],
  };
};
const getChartOptions = () => {
  return {
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      axis: 'x',
      intersect: false,
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      crosshair: {
        line: {
          color: '#E5E4FA57',
        },
        zoom: {
          enabled: false,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => context.raw,
        },
        position: 'nearest',
        external: tooltipHandler,
        backgroundColor: '#24243A',
        titleFont: { size: 13 },
        titleColor: '#fff',
        bodyFont: { size: 0 },
        displayColors: false,
        padding: 11,
        titleMarginBottom: 0,
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          maxTicksLimit: 5,
          color: '#9594AA',
          padding: 10,
        },
        grid: {
          display: false,
        },
      },
      y: {
        ticks: {
          autoSkip: true,
          maxTicksLimit: 5,
          color: '#9594AA',
          padding: 20,
          callback: function (label: number) {
            return `${label}`.substring(0, 8);
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    responsive: true,
  };
};

function setPriceChartPeriod(period: TPeriod) {
  priceChartPeriod.value = period;
}

function closePriceChart() {
  isPriceChartShown.value = false;
}
</script>
<style lang="scss" scoped>
.container {
  background: var(--surface-overlay);
  padding: 17px;
  border-radius: 24px;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  position: relative;
}
.navigation {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}
.tokens {
  background: #24243a;
  border-radius: 100px;
  padding: 4px;
  display: flex;
  gap: 10px;

  button {
    height: 32px;
    background: none;
    border: 0;
    border-radius: 100px;
    padding: 4px 12px 4px 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: #fbfaff;
    font-weight: 500;
    font-size: 15px;
    line-height: 18px;

    &:hover {
      background-color: #4a495e66;
    }
  }
  button.active {
    background: #4a495e;
  }
}
.token-img {
  width: 24px;
  height: 24px;
  border-radius: 100%;
}
.periods {
  background: #24243a;
  border-radius: 100px;
  min-width: 292px;
  display: flex;
  align-items: center;
  padding: 2px 4px;
  gap: 6px;

  button {
    width: 52px;
    height: 32px;
    background: none;
    border: 0;
    border-radius: 51px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    color: #fbfaff;

    &:hover {
      background-color: #4a495e66;
    }
  }
  button.active {
    background: #4a495e;
  }
}
.sub-navigation {
  display: flex;
  gap: 12px;

  &_desktop {
    display: flex;
    @media (max-width: 1024px) {
      display: none;
    }
  }

  &_mobile {
    display: none;
    @media (max-width: 1024px) {
      display: flex;
      justify-content: flex-end;
    }
  }
}
.close-mobile {
  color: #d4c4ed;
  height: 40px;
  width: 40px;
  background: #24243a;
  border-radius: 16px;
  border: 0;
  cursor: pointer;

  &:hover {
    background-color: #4a495e66;
  }

  img {
    width: 5px;
    height: 10px;
  }
}
.close-mobile {
  @media (min-width: 1024px) {
    display: none;
  }
}
.price {
  margin-bottom: 20px;
}
.current-price {
  font-size: 30px;
  line-height: 35px;
  font-weight: 550;
  color: #e5e4fa;
  margin-bottom: 4px;
}
.diff-price {
  font-size: 13px;
  line-height: 18px;
  &.down {
    color: var(--accent-error);
  }
  &.up {
    color: var(--accent-success);
  }
}
.msg-wrapper {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.msg-container {
  background: #24243a;
  border-radius: 16px;
  padding: 24px;
  width: 280px;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}

.chart {
  height: 100%;
  flex: 1;
  &_blurred {
    filter: blur(4px);
  }
}
</style>
